/**
 * Browser layer for /precio-vivienda (progressive enhancement over the static page that
 * src/precios/precios.ts renders): re-renders each chart at its real width, adds the
 * crosshair / per-bar tooltips, the period buttons, "Comparar con…" (lazy-loads
 * /precio-vivienda/series.json), the value calculator and the hub table's search/sort.
 * Untrusted strings (municipality names) only ever go through textContent.
 */
import { barChartSvg, barLayout, lineChartSvg, lineLayout, type LineSeries, type LineSpec, type Marker } from './charts.js';
import type { PricePayload } from './precios.js';

const fmtInt = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 });
const fmtPct = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 1, minimumFractionDigits: 1 });
const eurM2 = (v: number) => `${fmtInt.format(Math.round(v))} €/m²`;
const eurRough = (v: number) => `${fmtInt.format(Math.round(v / 1000) * 1000)} €`;
const pct = (r: number) => `${r > 0.0005 ? '+' : r < -0.0005 ? '−' : ''}${fmtPct.format(Math.abs(r * 100))} %`;
const qShort = (q: string) => `T${q.slice(-1)} ${q.slice(0, 4)}`;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

function svgEl(tag: string, attrs: Record<string, string | number>): SVGElement {
  const e = document.createElementNS('http://www.w3.org/2000/svg', tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  return e;
}

/** Tooltip rows: value strong first, series label after, keyed by a short line. */
function showTip(tip: HTMLElement, title: string, rows: { cls: string; value: string; label: string }[], x: number, plot: HTMLElement): void {
  tip.replaceChildren();
  tip.append(el('p', 'tip-title', title));
  for (const r of rows) {
    const p = el('p', 'tip-row');
    p.append(el('span', `key-line ${r.cls}`), el('strong', '', r.value), el('span', 'tip-label', r.label));
    tip.append(p);
  }
  tip.hidden = false;
  const w = plot.clientWidth;
  const tw = tip.offsetWidth;
  tip.style.left = `${Math.max(0, Math.min(w - tw, x + 12 > w - tw ? x - tw - 12 : x + 12))}px`;
}

// ---------------------------------------------------------------------------- line charts

interface LineChart {
  fig: HTMLElement;
  build: (width: number) => LineSpec;
  aria: string;
}

function mountLine(c: LineChart): () => void {
  const plot = c.fig.querySelector<HTMLElement>('.viz-plot');
  const tip = c.fig.querySelector<HTMLElement>('.viz-tip');
  if (!plot || !tip) return () => {};
  const render = () => {
    const width = Math.max(300, Math.round(plot.clientWidth));
    const spec = c.build(width);
    spec.height = spec.height ?? (width < 520 ? 240 : 300);
    plot.innerHTML = lineChartSvg(spec, c.aria); // our own markup from numbers; labels escaped in charts.ts
    const svg = plot.querySelector('svg');
    const layer = plot.querySelector('g.hover');
    if (!svg || !layer) return;
    const L = lineLayout(spec);
    const hair = svgEl('line', { class: 'hair', y1: L.pad.t, y2: L.height - L.pad.b, x1: 0, x2: 0, visibility: 'hidden' });
    layer.append(hair);
    const dots = spec.series.map((s) => {
      const d = svgEl('circle', { class: `hover-dot s${s.slot}`, r: 5, cx: 0, cy: 0, visibility: 'hidden' });
      layer.append(d);
      return d;
    });
    const move = (clientX: number) => {
      const box = svg.getBoundingClientRect();
      const px = ((clientX - box.left) / box.width) * L.width;
      const i = L.indexAt(px);
      const x = L.xAt(i);
      hair.setAttribute('x1', String(x));
      hair.setAttribute('x2', String(x));
      hair.setAttribute('visibility', 'visible');
      const rows: { cls: string; value: string; label: string }[] = [];
      spec.series.forEach((s, k) => {
        const v = s.values[i];
        const dot = dots[k];
        if (!dot) return;
        if (v === null || v === undefined) {
          dot.setAttribute('visibility', 'hidden');
          return;
        }
        dot.setAttribute('visibility', 'visible');
        dot.setAttribute('cx', String(x));
        dot.setAttribute('cy', String(L.yAt(v)));
        const prev = s.values[i - 4];
        rows.push({ cls: `s${s.slot}`, value: eurM2(v), label: `${s.label}${prev ? ` · ${pct(v / prev - 1)} en un año` : ''}` });
      });
      const marker = (spec.markers ?? []).find((m) => m.i === i);
      showTip(tip, marker ? marker.label : qShort(spec.x[i] ?? ''), rows, (x / L.width) * box.width, plot);
    };
    svg.addEventListener('pointermove', (e) => move(e.clientX));
    svg.addEventListener('pointerdown', (e) => move(e.clientX));
    svg.addEventListener('pointerleave', () => {
      tip.hidden = true;
      hair.setAttribute('visibility', 'hidden');
      dots.forEach((d) => d.setAttribute('visibility', 'hidden'));
    });
    // Keyboard: focus the chart, arrows walk the quarters.
    svg.setAttribute('tabindex', '0');
    let ki = spec.x.length - 1;
    svg.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      ki = Math.max(0, Math.min(spec.x.length - 1, ki + (e.key === 'ArrowRight' ? 1 : -1)));
      const box = svg.getBoundingClientRect();
      move(box.left + (L.xAt(ki) / L.width) * box.width);
    });
    svg.addEventListener('blur', () => (tip.hidden = true));
  };
  render();
  return render;
}

// ---------------------------------------------------------------------------- bar charts

function mountBars(fig: HTMLElement, labels: string[], values: number[], opts: { diverging?: boolean; fmt: (v: number) => string; tip: (i: number) => { title: string; value: string; label: string } }, aria: string): () => void {
  const plot = fig.querySelector<HTMLElement>('.viz-plot');
  const tip = fig.querySelector<HTMLElement>('.viz-tip');
  if (!plot || !tip) return () => {};
  const render = () => {
    const width = Math.max(300, Math.round(plot.clientWidth));
    const spec = { labels, values, diverging: opts.diverging, fmt: opts.fmt, width, height: width < 520 ? 180 : 220 };
    plot.innerHTML = barChartSvg(spec, aria);
    const svg = plot.querySelector('svg');
    const layer = plot.querySelector('g.hover');
    if (!svg || !layer) return;
    const L = barLayout(spec);
    // Hit target: the whole band, full height (bigger than the painted bar).
    values.forEach((_, i) => {
      const hit = svgEl('rect', { class: 'hit', x: L.xAt(i) - L.band / 2, y: L.pad.t, width: L.band, height: L.height - L.pad.t - L.pad.b, tabindex: 0 });
      const show = () => {
        svg.querySelectorAll('.bar').forEach((b) => b.classList.toggle('is-hover', b.getAttribute('data-i') === String(i)));
        const t = opts.tip(i);
        const box = svg.getBoundingClientRect();
        showTip(tip, t.title, [{ cls: opts.diverging ? ((values[i] ?? 0) >= 0 ? 'pos' : 'neg') : 's1', value: t.value, label: t.label }], (L.xAt(i) / L.width) * box.width, plot);
      };
      hit.addEventListener('pointerenter', show);
      hit.addEventListener('pointerdown', show);
      hit.addEventListener('focus', show);
      layer.append(hit);
    });
    svg.addEventListener('pointerleave', () => {
      tip.hidden = true;
      svg.querySelectorAll('.bar').forEach((b) => b.classList.remove('is-hover'));
    });
  };
  render();
  return render;
}

// ---------------------------------------------------------------------------- municipality page

type SeriesIndex = { q: string[]; m: [string, string, string, boolean, (number | null)[]][] };

function pricePage(p: PricePayload): void {
  const renders: (() => void)[] = [];
  let range: 'all' | '5' | '10' = 'all';
  let showSpain = true;
  let compare: { name: string; values: (number | null)[] } | null = null;

  const mainFig = document.getElementById('main-chart');
  if (mainFig) {
    const markersIn = (from: number): Marker[] => p.markers.filter((m) => m.i >= from).map((m) => ({ ...m, i: m.i - from }));
    renders.push(
      mountLine({
        fig: mainFig,
        aria: `Evolución del precio de la vivienda en ${p.name}, €/m² por trimestre`,
        build: (width) => {
          const from = range === 'all' ? 0 : Math.max(0, p.q.length - Number(range) * 4);
          const series: LineSeries[] = [{ key: 'self', label: p.name, slot: 1, values: p.total.slice(from) }];
          if (compare) series.push({ key: 'cmp', label: compare.name, slot: 2, values: compare.values.slice(from) });
          if (showSpain) series.push({ key: 'spain', label: 'Mediana España', slot: 3, values: p.spain.slice(from) });
          return { x: p.q.slice(from), series, markers: markersIn(from), width };
        },
      }),
    );
    const controls = document.getElementById('main-controls');
    const legend = document.getElementById('main-legend');
    const syncLegend = () => {
      if (!legend) return;
      const items: [string, string][] = [['s1', p.name]];
      if (compare) items.push(['s2', compare.name]);
      if (showSpain) items.push(['s3', 'Mediana España']);
      legend.replaceChildren(
        ...items.map(([c, l]) => {
          const li = el('li');
          li.append(el('span', `key-line ${c}`), document.createTextNode(l));
          return li;
        }),
      );
    };
    const redrawMain = () => {
      renders[0]?.();
      syncLegend();
    };
    if (controls) {
      controls.hidden = false;
      controls.querySelectorAll<HTMLButtonElement>('[data-range]').forEach((b) =>
        b.addEventListener('click', () => {
          range = (b.dataset.range as typeof range) ?? 'all';
          controls.querySelectorAll('[data-range]').forEach((o) => o.setAttribute('aria-pressed', String(o === b)));
          redrawMain();
        }),
      );
      document.getElementById('toggle-spain')?.addEventListener('change', (e) => {
        showSpain = (e.target as HTMLInputElement).checked;
        redrawMain();
      });
      const select = document.getElementById('compare') as HTMLSelectElement | null;
      let index: Promise<SeriesIndex> | null = null;
      const load = () => (index ??= fetch(p.seriesUrl).then((r) => r.json() as Promise<SeriesIndex>));
      const fill = async () => {
        if (!select || select.options.length > 1) return;
        const idx = await load();
        for (const [slug, name, prov] of idx.m) {
          if (slug === p.slug) continue;
          const o = el('option', '', `${name} (${prov})`);
          o.value = slug;
          select.append(o);
        }
      };
      select?.addEventListener('focus', () => void fill(), { once: true });
      select?.addEventListener('pointerdown', () => void fill(), { once: true });
      select?.addEventListener('change', async () => {
        const idx = await load();
        const row = idx.m.find(([slug]) => slug === select.value);
        compare = row ? { name: row[1], values: p.q.map((q) => row[4][idx.q.indexOf(q)] ?? null) } : null;
        redrawMain();
      });
    }
  }

  const yearFig = document.getElementById('year-chart');
  if (yearFig && p.years.length > 1) {
    const ys = p.years.slice(1);
    const ch = ys.map((y, i) => y.value / (p.years[i]?.value ?? y.value) - 1);
    renders.push(
      mountBars(
        yearFig,
        ys.map((y) => `${y.year}${y.partial ? '*' : ''}`),
        ch.map((c) => c * 100),
        {
          diverging: true,
          fmt: (v) => `${fmtInt.format(v)} %`,
          tip: (i) => ({ title: `${ys[i]?.year ?? ''}${ys[i]?.partial ? ' (en curso)' : ''}`, value: pct(ch[i] ?? 0), label: `media ${eurM2(ys[i]?.value ?? 0)}` }),
        },
        `Variación anual del precio en ${p.name}`,
      ),
    );
  }

  const nuFig = document.getElementById('newused-chart');
  if (nuFig) {
    const from = Math.max(0, p.q.length - 40);
    renders.push(
      mountLine({
        fig: nuFig,
        aria: `Vivienda nueva y usada en ${p.name}`,
        build: (width) => ({
          x: p.q.slice(from),
          series: [
            { key: 'nuevo', label: 'Hasta 5 años', slot: 1, values: p.nuevo.slice(from) },
            { key: 'usado', label: 'Más de 5 años', slot: 2, values: p.usado.slice(from) },
          ],
          width,
          height: width < 520 ? 220 : 240,
        }),
      }),
    );
  }

  const actFig = document.getElementById('activity-chart');
  if (actFig) {
    const from = Math.max(0, p.q.length - 20);
    const qs = p.q.slice(from);
    const tas = p.tas.slice(from).map((v) => v ?? 0);
    renders.push(
      mountBars(
        actFig,
        qs.map((q) => (q.endsWith('-T1') ? q.slice(0, 4) : '')),
        tas,
        { fmt: (v) => fmtInt.format(v), tip: (i) => ({ title: qShort(qs[i] ?? ''), value: fmtInt.format(tas[i] ?? 0), label: 'tasaciones' }) },
        `Tasaciones por trimestre en ${p.name}`,
      ),
    );
  }

  calculator(p);

  let t = 0;
  let lastW = window.innerWidth;
  window.addEventListener('resize', () => {
    if (window.innerWidth === lastW) return; // mobile URL-bar resizes
    lastW = window.innerWidth;
    clearTimeout(t);
    t = window.setTimeout(() => renders.forEach((r) => r()), 120);
  });
}

function calculator(p: PricePayload): void {
  const m2 = document.getElementById('calc-m2') as HTMLInputElement | null;
  const range = document.getElementById('calc-range') as HTMLInputElement | null;
  const type = document.getElementById('calc-type') as HTMLSelectElement | null;
  const year = document.getElementById('calc-year') as HTMLSelectElement | null;
  const out = document.getElementById('calc-value');
  const band = document.getElementById('calc-band');
  const gain = document.getElementById('calc-gain');
  if (!m2 || !range || !type || !year || !out || !band || !gain) return;
  const latestOf = (arr: (number | null)[]) => {
    for (let i = arr.length - 1; i >= 0; i--) if (arr[i] != null) return arr[i] as number;
    return null;
  };
  const update = () => {
    const area = Math.max(1, Number(m2.value) || p.flatM2);
    const series = type.value === 'nuevo' ? p.nuevo : type.value === 'usado' ? p.usado : p.total;
    const per = latestOf(series) ?? latestOf(p.total) ?? 0;
    const now = per * area;
    out.textContent = eurRough(now);
    band.textContent = `entre ${eurRough(now * 0.85)} y ${eurRough(now * 1.15)} · ${eurM2(per)}`;
    const y = Number(year.value);
    const then = p.years.find((x) => x.year === y)?.value;
    const totalNow = latestOf(p.total) ?? per;
    gain.textContent = then ? `En ${y} se tasaba en ${eurRough(then * area)}: ${pct(totalNow / then - 1)} desde entonces (${eurRough((totalNow - then) * area)}).` : '';
  };
  m2.addEventListener('input', () => {
    range.value = m2.value;
    update();
  });
  range.addEventListener('input', () => {
    m2.value = range.value;
    update();
  });
  type.addEventListener('change', update);
  year.addEventListener('change', update);
  update();
}

// ---------------------------------------------------------------------------- hub

function hub(): void {
  const table = document.getElementById('hub-table');
  const controls = document.getElementById('hub-controls');
  const search = document.getElementById('hub-search') as HTMLInputElement | null;
  const prov = document.getElementById('hub-prov') as HTMLSelectElement | null;
  const count = document.getElementById('hub-count');
  const tbody = table?.querySelector('tbody');
  if (!table || !tbody || !controls || !search || !prov || !count) return;
  controls.hidden = false;
  const rows = [...tbody.querySelectorAll<HTMLTableRowElement>('tr')];
  const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const filter = () => {
    const q = norm(search.value.trim());
    let n = 0;
    for (const r of rows) {
      const ok = (!q || norm(r.dataset.name ?? '').includes(q)) && (!prov.value || r.dataset.prov === prov.value);
      r.hidden = !ok;
      if (ok) n++;
    }
    count.textContent = `${n} de ${rows.length}`;
  };
  search.addEventListener('input', filter);
  prov.addEventListener('change', filter);
  let key = 'v';
  let dir = -1;
  table.querySelectorAll<HTMLButtonElement>('[data-sort]').forEach((b) =>
    b.addEventListener('click', () => {
      const k = b.dataset.sort ?? 'v';
      dir = k === key ? -dir : k === 'name' || k === 'prov' || k === 'rank' ? 1 : -1;
      key = k;
      table.querySelectorAll('[data-sort]').forEach((o) => o.removeAttribute('aria-sort'));
      b.setAttribute('aria-sort', dir > 0 ? 'ascending' : 'descending');
      const val = (r: HTMLTableRowElement): string | number => {
        const raw = r.dataset[key] ?? '';
        return key === 'name' || key === 'prov' ? raw : raw === '' ? -Infinity : Number(raw);
      };
      rows.sort((a, c) => {
        const x = val(a);
        const y = val(c);
        return (typeof x === 'string' && typeof y === 'string' ? x.localeCompare(y, 'es') : Number(x) - Number(y)) * dir;
      });
      tbody.append(...rows);
    }),
  );
  filter();
}

// ---------------------------------------------------------------------------- boot

const dataEl = document.getElementById('price-data');
if (dataEl?.textContent) {
  pricePage(JSON.parse(dataEl.textContent) as PricePayload);
} else {
  hub();
}
